import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useUser } from "@/contexts/UserContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, Truck, Search, ArrowLeft, Calendar, MapPin, CheckCircle, XCircle, Star, ChevronDown } from "lucide-react";
import StarRating from "@/components/StarRating";
import ShipmentLoadDetails from "@/components/ShipmentLoadDetails";
import type { Load, LoadItem, Truck as TruckType, User } from "@shared/schema";

interface CompletedShipment {
  loadId: string;
  loadIdentifier: string | null;
  route: string;
  completedAt: Date | string | null;
  otherPartyName: string;
  otherPartyType: string;
  rating: number | null;
  threadId: string;
}

type LoadWithPoster = Load & { poster?: User; items?: LoadItem[] };

export default function History() {
  const [, setLocation] = useLocation();
  const { userType } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedLoadId, setExpandedLoadId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"loads" | "trucks" | "shipments">(
    userType === 'Carrier' ? "trucks" : "loads"
  );

  const { data: currentUser } = useQuery<User>({
    queryKey: ['/api/auth/user'],
  });

  const { data: userLoads } = useQuery<LoadWithPoster[]>({
    queryKey: ['/api/loads/user', currentUser?.id],
    enabled: !!currentUser && (userType === 'Shipper' || userType === 'Broker'),
  });

  const { data: claimedLoads } = useQuery<LoadWithPoster[]>({
    queryKey: ['/api/loads/claimed/me'],
    enabled: !!currentUser && (userType === 'Carrier' || userType === 'Broker'),
  });

  const { data: userTrucks } = useQuery<TruckType[]>({ 
    queryKey: ['/api/trucks/user', currentUser?.id],
    enabled: !!currentUser && (userType === 'Carrier' || userType === 'Broker'),
  });

  const { data: completedData } = useQuery<{ shipments: CompletedShipment[]; summary: { total: number; averageGiven: number | null } }>({
    queryKey: ['/api/completed-shipments'],
    enabled: !!currentUser,
  });

  const getAllLoads = () => {
    if (userType === 'Shipper') return userLoads || [];
    if (userType === 'Carrier') return claimedLoads || [];
    const loadMap = new Map<string, LoadWithPoster>();
    (userLoads || []).forEach(l => loadMap.set(l.id, l));
    (claimedLoads || []).forEach(l => loadMap.set(l.id, l));
    return Array.from(loadMap.values());
  };
  const allLoads = getAllLoads();

  const pastLoads = allLoads?.filter(l => l.status === "Completed" || l.status === "Closed") || [];
  const closedTrucks = userTrucks?.filter(t => t.status === "Closed") || [];
  const completedShipments = completedData?.shipments || [];

  const filteredLoads = pastLoads.filter(load => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      load.origin?.toLowerCase().includes(term) ||
      load.destination?.toLowerCase().includes(term) ||
      load.loadIdentifier?.toLowerCase().includes(term)
    );
  });

  const filteredTrucks = closedTrucks.filter(truck => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      truck.origin?.toLowerCase().includes(term) ||
      truck.destination?.toLowerCase().includes(term)
    );
  });

  const filteredShipments = completedShipments.filter(shipment => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      shipment.route?.toLowerCase().includes(term) ||
      shipment.loadIdentifier?.toLowerCase().includes(term) ||
      shipment.otherPartyName?.toLowerCase().includes(term)
    );
  });

  const formatDate = (date: Date | string | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setLocation('/dashboard')}
            data-testid="button-back-dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">History</h1>
            <p className="text-muted-foreground">View your past shipments and closed truck postings</p>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by location or load ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="input-history-search"
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "loads" | "trucks" | "shipments")}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="loads" className="flex-1" data-testid="tab-loads">
              <span className="hidden sm:inline-flex mr-2"><Package className="h-4 w-4" /></span>
              Past Loads ({filteredLoads.length})
            </TabsTrigger>
            {(userType === 'Carrier' || userType === 'Broker') && (
              <TabsTrigger value="trucks" className="flex-1" data-testid="tab-trucks">
                <span className="hidden sm:inline-flex mr-2"><Truck className="h-4 w-4" /></span>
                Closed Trucks ({filteredTrucks.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="shipments" className="flex-1" data-testid="tab-completed-shipments">
              <span className="hidden sm:inline-flex mr-2"><Star className="h-4 w-4" /></span>
              Rated Shipments ({filteredShipments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="loads">
            {filteredLoads.length > 0 ? (
              <div className="space-y-3">
                {filteredLoads.map(load => {
                  const isExpanded = expandedLoadId === load.id;
                  return (
                    <Card
                      key={load.id}
                      className="overflow-hidden"
                      data-testid={`card-history-load-${load.id}`}
                    >
                      <button
                        type="button"
                        className="w-full p-4 text-left hover:bg-muted/40 transition-colors"
                        onClick={() => setExpandedLoadId(isExpanded ? null : load.id)}
                        aria-expanded={isExpanded}
                        aria-controls={`history-load-details-${load.id}`}
                        data-testid={`button-history-load-${load.id}`}
                      >
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="flex-1 min-w-0 md:min-w-[200px]">
                            <div className="flex items-center gap-2 mb-2">
                              {load.loadIdentifier && (
                                <Badge variant="secondary" className="text-xs">
                                  {load.loadIdentifier}
                                </Badge>
                              )}
                              {load.status === "Completed" ? (
                                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Completed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-200">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Closed
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm flex-wrap">
                              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="font-medium min-w-0 truncate">{load.origin}</span>
                              <span className="text-muted-foreground shrink-0">→</span>
                              <span className="font-medium min-w-0 truncate">{load.destination}</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="text-right text-sm text-muted-foreground">
                              <div className="flex items-center gap-1 justify-end">
                                <Calendar className="h-3 w-3" />
                                {formatDate(load.completedAt || load.deliveryDate)}
                              </div>
                              <div className="mt-1">
                                {load.weight?.toLocaleString()} lbs
                              </div>
                            </div>
                            <ChevronDown
                              className={`h-4 w-4 mt-0.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            />
                          </div>
                        </div>
                      </button>
                      {isExpanded && (
                        <div
                          id={`history-load-details-${load.id}`}
                          className="border-t px-4 py-4"
                          data-testid={`history-load-details-${load.id}`}
                        >
                          <ShipmentLoadDetails load={load} />
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-semibold mb-2">No past loads</h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm ? "No loads match your search" : "Completed and closed loads will appear here"}
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="trucks">
            {filteredTrucks.length > 0 ? (
              <div className="space-y-3">
                {filteredTrucks.map(truck => (
                  <Card 
                    key={truck.id} 
                    className="p-4"
                    data-testid={`card-history-truck-${truck.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0 md:min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary">
                            {truck.capacity?.toLocaleString()} lbs
                          </Badge>
                          <Badge variant="outline" className="text-muted-foreground">
                            Closed
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium min-w-0 truncate">{truck.origin}</span>
                          <span className="text-muted-foreground shrink-0">→</span>
                          <span className="font-medium min-w-0 truncate">{truck.destination}</span>
                        </div>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <div className="flex items-center gap-1 justify-end">
                          <Calendar className="h-3 w-3" />
                          {formatDate(truck.availableFrom)}
                        </div>
                        <div className="mt-1">
                          {truck.capacity?.toLocaleString()} lbs capacity
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-semibold mb-2">No closed trucks</h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm ? "No trucks match your search" : "Closed truck postings will appear here"}
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="shipments">
            {completedData?.summary && (completedData.summary.total > 0 || completedData.summary.averageGiven != null) && (
              <Card className="p-4 mb-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Completed</p>
                    <p className="text-2xl font-bold" data-testid="text-total-completed">{completedData.summary.total}</p>
                  </div>
                  {completedData.summary.averageGiven != null && (
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground mb-1">Avg Rating Given</p>
                      <div className="flex items-center gap-2 justify-end">
                        <StarRating value={completedData.summary.averageGiven} readOnly fractional size="sm" />
                        <span className="font-semibold" data-testid="text-avg-rating-given">
                          {completedData.summary.averageGiven.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
            {filteredShipments.length > 0 ? (
              <div className="space-y-3">
                {filteredShipments.map(shipment => (
                  <Card 
                    key={`${shipment.loadId}-${shipment.threadId}`}
                    className="p-4"
                    data-testid={`card-completed-shipment-${shipment.loadId}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0 md:min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          {shipment.loadIdentifier && (
                            <Badge variant="secondary" className="text-xs">
                              {shipment.loadIdentifier}
                            </Badge>
                          )}
                          <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Completed
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm mb-1 flex-wrap">
                          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium min-w-0 truncate">{shipment.route}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          With {shipment.otherPartyName}
                          {shipment.otherPartyType && (
                            <span className="ml-1">({shipment.otherPartyType})</span>
                          )}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        <div className="flex items-center gap-1 justify-end text-muted-foreground mb-2">
                          <Calendar className="h-3 w-3" />
                          {formatDate(shipment.completedAt)}
                        </div>
                        {shipment.rating != null ? (
                          <div className="flex flex-col items-end gap-1">
                            <p className="text-xs text-muted-foreground">Your rating</p>
                            <StarRating value={shipment.rating} readOnly size="sm" />
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">No rating given</p>
                        )}
                      </div>
                    </div>
                    {shipment.loadId && (
                      <button
                        onClick={() => setLocation(`/loads/${shipment.loadId}/bids`)}
                        className="text-xs font-medium text-primary underline hover:no-underline mt-2 block"
                        data-testid={`link-view-bid-summary-${shipment.loadId}`}
                      >
                        View bid summary
                      </button>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-semibold mb-2">No rated shipments yet</h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm ? "No shipments match your search" : "Shipments you've rated will appear here once closed"}
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
